import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, SlidersHorizontal, Pencil, Archive, ArchiveRestore, Trash2, Wand2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { inr, inr0 } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/lib/settings";
import { PageHeader, Empty } from "@/components/Bits";
import StatusChip from "@/components/StatusChip";
import Modal from "@/components/Modal";

const splitList = (s) => [...new Set(String(s || "").split(",").map((x) => x.trim()).filter(Boolean))];
const num = (v) => (v === "" || v == null ? 0 : Number(v));

function TaxSelect({ value, onChange, settings, testid }) {
  const rates = [...new Set([0, ...(settings?.tax_rates || [5, 12, 18]), Number(value)])].sort((a, b) => a - b);
  return (
    <select className="field" value={value} onChange={(e) => onChange(Number(e.target.value))} data-testid={testid}>
      {rates.map((r) => <option key={r} value={r}>{r}% GST</option>)}
    </select>
  );
}

function StyleFields({ f, set, settings, showCode }) {
  const cats = [...new Set([...(settings?.product_categories || []), f.category].filter(Boolean))];
  const types = [...new Set([...(settings?.product_types || []), f.type].filter(Boolean))];
  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="col-span-2"><label className="label">Product name *</label><input className="field" value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Handloom Cotton Shirt" data-testid="prod-name" /></div>
      <div><label className="label">Brand</label><input className="field" value={f.brand} onChange={(e) => set("brand", e.target.value)} data-testid="prod-brand" /></div>
      <div><label className="label">HSN code</label><input className="field font-num" value={f.hsn} onChange={(e) => set("hsn", e.target.value)} placeholder="e.g. 6205" data-testid="prod-hsn" /></div>
      <div><label className="label">Category</label><select className="field" value={f.category} onChange={(e) => set("category", e.target.value)} data-testid="prod-category">{cats.map((c) => <option key={c}>{c}</option>)}</select></div>
      <div><label className="label">Type</label><select className="field" value={f.type} onChange={(e) => set("type", e.target.value)} data-testid="prod-type">{types.map((c) => <option key={c}>{c}</option>)}</select></div>
      {showCode && <div><label className="label">Style code</label><input className="field font-num" value={f.style_code} onChange={(e) => set("style_code", e.target.value)} placeholder="Auto-generated if blank" data-testid="prod-code" /></div>}
      <div className={showCode ? "" : "col-span-2"}><label className="label">Image URL</label><input className="field" value={f.image} onChange={(e) => set("image", e.target.value)} placeholder="https://…" /></div>
      <div className="col-span-2"><label className="label">Description</label><input className="field" value={f.description} onChange={(e) => set("description", e.target.value)} /></div>
    </div>
  );
}

const blankStyle = (s) => ({
  name: "", brand: "", category: s?.product_categories?.[0] || "Women", type: s?.product_types?.[0] || "Other",
  hsn: "", style_code: "", image: "", description: "", season: "", collection: "", unit: "pcs", status: "active",
});

function AddProduct({ open, onClose, onDone }) {
  const s = useSettings();
  const [f, setF] = useState(null);
  const [tax, setTax] = useState(5);
  const [reorder, setReorder] = useState(3);
  const [colors, setColors] = useState("");
  const [sizes, setSizes] = useState("");
  const [price, setPrice] = useState({ cost_price: "", selling_price: "", mrp: "", opening_qty: "" });
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && s) {
      setF(blankStyle(s)); setTax(s.default_tax_rate ?? 5); setReorder(s.default_reorder_point ?? 3);
      setColors(""); setSizes(""); setPrice({ cost_price: "", selling_price: "", mrp: "", opening_qty: "" }); setRows([]);
    }
  }, [open, s]);

  // Build the variant rows as colours and sizes are typed — no extra button to press.
  // One dimension is enough: sizes only, or colours only.
  const priceRef = useRef(price);
  priceRef.current = price;
  useEffect(() => {
    const cs = splitList(colors), ss = splitList(sizes);
    if (!cs.length && !ss.length) { setRows([]); return; }
    const colorList = cs.length ? cs : ["Standard"];
    const sizeList = ss.length ? ss : ["Free"];
    setRows((prev) => {
      const keep = Object.fromEntries(prev.map((r) => [`${r.color}|${r.size}`, r]));
      return colorList.flatMap((color) => sizeList.map((size) => keep[`${color}|${size}`] || { color, size, ...priceRef.current }));
    });
  }, [colors, sizes]);

  if (!open || !f || !s) return null;

  const set = (k, v) => setF({ ...f, [k]: v });
  const upd = (i, patch) => setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  // Typing a default fills every row that still holds the previous default (or nothing),
  // so per-row edits survive.
  const setDefault = (k, v) => {
    const old = price[k];
    setPrice({ ...price, [k]: v });
    setRows((prev) => prev.map((r) => (r[k] === old || r[k] === "" || r[k] == null ? { ...r, [k]: v } : r)));
  };
  const applyAll = (k) => setRows(rows.map((r) => ({ ...r, [k]: price[k] })));
  const totalQty = rows.reduce((a, r) => a + num(r.opening_qty), 0);
  const totalCost = rows.reduce((a, r) => a + num(r.opening_qty) * num(r.cost_price), 0);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.post("/products", {
        style: f, tax_rate: tax, reorder_point: Number(reorder),
        variants: rows.map((v) => ({ color: v.color, size: v.size, cost_price: num(v.cost_price), selling_price: num(v.selling_price), mrp: num(v.mrp), opening_qty: parseInt(v.opening_qty || 0, 10) })),
      });
      toast.success(`${r.data.name} added · ${r.data.variants.length} variants · ${r.data.total_qty} pcs`);
      onDone(r.data); onClose();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const blocker = !f.name.trim() ? "Enter a product name."
    : !rows.length ? "Enter at least one colour or size to create variants."
      : rows.some((r) => num(r.selling_price) <= 0) ? "Every variant needs a selling price."
        : "";
  const scales = Object.entries(s.size_scales || {});
  return (
    <Modal open={open} onClose={onClose} title="Add Product" wide testid="add-product">
      <StyleFields f={f} set={set} settings={s} showCode />

      <div className="grid grid-cols-2 gap-4 mt-4">
        <div><label className="label">GST rate</label><TaxSelect value={tax} onChange={setTax} settings={s} testid="prod-tax" /></div>
        <div><label className="label">Reorder alert at (pcs)</label><input type="number" min="0" className="field font-num" value={reorder} onChange={(e) => setReorder(e.target.value)} /></div>
      </div>

      <div className="eyebrow mt-6 mb-2">Variants — size × colour</div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label">Colours (comma separated)</label>
          <input className="field" value={colors} onChange={(e) => setColors(e.target.value)} placeholder="e.g. Navy, Ivory — leave blank if only one" data-testid="prod-colors" />
          {s.color_library?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5">{s.color_library.map((c) => (
              <button key={c} type="button" className="chip border bg-white border-[#E6E1D8] text-[#5C626A] hover:bg-[#F4F0EA]" onClick={() => setColors(splitList(`${colors},${c}`).join(", "))}>+ {c}</button>
            ))}</div>
          )}
        </div>
        <div>
          <label className="label">Sizes (comma separated)</label>
          <input className="field font-num" value={sizes} onChange={(e) => setSizes(e.target.value)} placeholder="e.g. S, M, L, XL — leave blank for free size" data-testid="prod-sizes" />
          <div className="flex flex-wrap gap-1 mt-1.5">{scales.map(([k, v]) => (
            <button key={k} type="button" className="chip border bg-white border-[#E6E1D8] text-[#5C626A] hover:bg-[#F4F0EA] capitalize" onClick={() => setSizes(v.join(", "))}>{k}: {v.join(" ")}</button>
          ))}</div>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-3 mt-4 items-end">
        {[["cost_price", "Cost price"], ["selling_price", "Selling price"], ["mrp", "MRP"], ["opening_qty", "Opening qty each"]].map(([k, lbl]) => (
          <div key={k}>
            <label className="label">{lbl}</label>
            <div className="flex gap-1">
              <input type="number" min="0" className="field font-num" value={price[k]} onChange={(e) => setDefault(k, e.target.value)} data-testid={`prod-default-${k}`} />
              {rows.length > 0 && <button type="button" className="btn btn-ghost btn-sm" title="Apply to every variant, replacing edits" onClick={() => applyAll(k)}>All</button>}
            </div>
          </div>
        ))}
      </div>

      {rows.length === 0 && (
        <div className="mt-3 text-sm text-[#8C929B] flex items-center gap-2" data-testid="prod-variant-hint">
          <Wand2 size={14} /> Enter colours and/or sizes above — the variants appear here automatically.
        </div>
      )}

      {rows.length > 0 && (
        <div className="border border-[#E6E1D8] rounded-lg overflow-auto max-h-80 mt-4">
          <table className="tbl">
            <thead><tr><th>Colour</th><th>Size</th><th className="num">Cost</th><th className="num">Selling</th><th className="num">MRP</th><th className="num">Opening qty</th><th></th></tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r.color}|${r.size}`}>
                  <td>{r.color}</td><td className="font-num">{r.size}</td>
                  {["cost_price", "selling_price", "mrp", "opening_qty"].map((k) => (
                    <td key={k} className="num"><input type="number" min="0" className="field py-1 px-2 w-24 text-right font-num" value={r[k]} onChange={(e) => upd(i, { [k]: e.target.value })} data-testid={`prod-row-${i}-${k}`} /></td>
                  ))}
                  <td className="num"><button className="text-[#A3411D]" onClick={() => setRows(rows.filter((_, j) => j !== i))}><Trash2 size={14} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center justify-between mt-6">
        <div className="text-sm text-[#5C626A]">{rows.length} variants · <span className="font-num">{totalQty}</span> pcs opening stock · <span className="font-num">{inr(totalCost)}</span> at cost</div>
        <div className="flex gap-2">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={busy || !!blocker} data-testid="prod-save">Save product</button>
        </div>
      </div>
      {blocker && <div className="text-xs text-[#A3411D] text-right mt-2" data-testid="prod-blocker">{blocker}</div>}
    </Modal>
  );
}

function EditStyle({ style, onClose, onDone }) {
  const s = useSettings();
  const [f, setF] = useState(null);
  useEffect(() => { if (style) setF({ ...blankStyle(s), ...style }); }, [style, s]);
  if (!style || !f) return null;
  const save = async () => {
    try {
      const { name, brand, category, type, hsn, image, description, season, collection, unit, status, style_code } = f;
      await api.put(`/styles/${style.id}`, { name, brand, category, type, hsn, image, description, season, collection, unit, status, style_code });
      toast.success("Product updated"); onDone(); onClose();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Modal open={!!style} onClose={onClose} title={`Edit · ${style.style_code}`} testid="edit-style">
      <StyleFields f={f} set={(k, v) => setF({ ...f, [k]: v })} settings={s} />
      <div className="flex justify-end gap-2 mt-6"><button className="btn btn-ghost" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={!f.name.trim()}>Save</button></div>
    </Modal>
  );
}

function VariantForm({ open, style, variant, onClose, onDone }) {
  const s = useSettings();
  const isNew = !variant;
  const [f, setF] = useState(null);
  useEffect(() => {
    if (!open) return;
    const base = style?.variants?.[0];
    setF(variant ? { ...variant } : {
      color: "", size: "", cost_price: base?.cost_price ?? "", selling_price: base?.selling_price ?? "", mrp: base?.mrp ?? "",
      tax_rate: base?.tax_rate ?? s?.default_tax_rate ?? 5, reorder_point: base?.reorder_point ?? s?.default_reorder_point ?? 3, current_qty: 0, barcode: "",
    });
  }, [open, variant, style, s]);
  if (!open || !f) return null;
  const set = (k, v) => setF({ ...f, [k]: v });
  const save = async () => {
    try {
      const body = { cost_price: num(f.cost_price), selling_price: num(f.selling_price), mrp: num(f.mrp), tax_rate: Number(f.tax_rate), reorder_point: parseInt(f.reorder_point || 0, 10), barcode: f.barcode };
      if (isNew) await api.post("/skus", { ...body, style_id: style.id, color: f.color.trim(), size: f.size.trim(), current_qty: parseInt(f.current_qty || 0, 10) });
      else await api.put(`/skus/${variant.id}`, body);
      toast.success(isNew ? "Variant added" : "Variant updated"); onDone(); onClose();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Modal open={open} onClose={onClose} title={isNew ? `Add variant · ${style.name}` : `${variant.color} / ${variant.size}`} testid="variant-form">
      <div className="grid grid-cols-2 gap-4">
        {isNew && <>
          <div><label className="label">Colour</label><input className="field" value={f.color} onChange={(e) => set("color", e.target.value)} data-testid="var-color" /></div>
          <div><label className="label">Size</label><input className="field font-num" value={f.size} onChange={(e) => set("size", e.target.value)} data-testid="var-size" /></div>
        </>}
        <div><label className="label">Cost price</label><input type="number" min="0" className="field font-num" value={f.cost_price} onChange={(e) => set("cost_price", e.target.value)} /></div>
        <div><label className="label">Selling price</label><input type="number" min="0" className="field font-num" value={f.selling_price} onChange={(e) => set("selling_price", e.target.value)} data-testid="var-price" /></div>
        <div><label className="label">MRP</label><input type="number" min="0" className="field font-num" value={f.mrp} onChange={(e) => set("mrp", e.target.value)} /></div>
        <div><label className="label">GST</label><TaxSelect value={f.tax_rate} onChange={(v) => set("tax_rate", v)} settings={s} /></div>
        <div><label className="label">Reorder alert at</label><input type="number" min="0" className="field font-num" value={f.reorder_point} onChange={(e) => set("reorder_point", e.target.value)} /></div>
        <div><label className="label">Barcode</label><input className="field font-num" value={f.barcode} onChange={(e) => set("barcode", e.target.value)} placeholder={isNew ? "Auto if blank" : ""} /></div>
        {isNew && <div><label className="label">Opening stock</label><input type="number" min="0" className="field font-num" value={f.current_qty} onChange={(e) => set("current_qty", e.target.value)} /></div>}
      </div>
      {!isNew && <p className="text-xs text-[#8C929B] mt-3">Cost is normally updated automatically from purchases (weighted average). Change it here only to correct a mistake.</p>}
      <div className="flex justify-end gap-2 mt-6"><button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={num(f.selling_price) <= 0 || (isNew && (!f.color.trim() || !f.size.trim()))} data-testid="var-save">Save</button></div>
    </Modal>
  );
}

function AdjustModal({ open, onClose, sku, onDone }) {
  const [qty, setQty] = useState("");
  const [reason, setReason] = useState("correction");
  const [note, setNote] = useState("");
  useEffect(() => { if (open) { setQty(""); setReason("correction"); setNote(""); } }, [open]);
  if (!sku) return null;
  const submit = async () => {
    try {
      await api.post("/stock/adjust", { sku_id: sku.id, qty_change: parseInt(qty, 10), reason, note });
      toast.success("Stock adjusted");
      onDone();
      onClose();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Modal open={open} onClose={onClose} title="Adjust Stock" testid="adjust-modal">
      <div className="mb-4 text-sm">
        <span className="font-num">{sku.style_code}</span> · {sku.color} / {sku.size} · on hand{" "}
        <b className="font-num">{sku.current_qty}</b>
      </div>
      <label className="label">Quantity change (use − to reduce)</label>
      <input className="field mb-4 font-num" type="number" value={qty} onChange={(e) => setQty(e.target.value)} placeholder="e.g. -2 or 10" data-testid="adjust-qty" />
      <label className="label">Reason</label>
      <select className="field mb-4" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="adjust-reason">
        <option value="opening">Opening stock</option>
        <option value="damage">Damaged / lost</option>
        <option value="correction">Stock-take correction</option>
      </select>
      <label className="label">Note</label>
      <input className="field mb-2" value={note} onChange={(e) => setNote(e.target.value)} data-testid="adjust-note" />
      <p className="text-xs text-[#8C929B] mb-6">To add stock you bought from a vendor, use Purchases so the cost and payable are recorded.</p>
      <div className="flex justify-end gap-2">
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={submit} disabled={!qty || Number(qty) === 0} data-testid="adjust-submit">Post to ledger</button>
      </div>
    </Modal>
  );
}

function StyleDetail({ style, onClose, isOwner, refresh }) {
  const [adjust, setAdjust] = useState(null);
  const [editVariant, setEditVariant] = useState(null);
  const [addVariant, setAddVariant] = useState(false);
  const [editStyle, setEditStyle] = useState(null);
  if (!style) return null;
  const sizes = [...new Set(style.variants.map((v) => v.size))];
  const colors = [...new Set(style.variants.map((v) => v.color))];
  const cell = (c, s) => style.variants.find((v) => v.color === c && v.size === s);
  const archived = style.status === "archived";

  const toggleArchive = async () => {
    try {
      await api.post(`/styles/${style.id}/${archived ? "restore" : "archive"}`);
      toast.success(archived ? "Product restored" : "Product archived — hidden from sale search");
      refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Modal open={!!style} onClose={onClose} title={style.name} wide testid="style-detail">
      <div className="flex gap-5 mb-6">
        {style.image && <img src={style.image} alt={style.name} className="w-28 h-36 object-cover rounded-lg border border-[#E6E1D8]" />}
        <div className="text-sm space-y-1.5 flex-1">
          <div className="font-num text-[#8C929B]">{style.style_code}</div>
          <div className="text-[#5C626A]">{[style.brand, style.category, style.type].filter(Boolean).join(" · ")}</div>
          {style.hsn && <div className="text-[#5C626A]">HSN {style.hsn}</div>}
          {style.description && <div className="text-[#5C626A] max-w-md">{style.description}</div>}
          <div className="pt-1"><StatusChip status={archived ? "Archived" : "Active"} /></div>
        </div>
        {isOwner && (
          <div className="flex flex-col gap-2">
            <button className="btn btn-ghost btn-sm" onClick={() => setEditStyle(style)} data-testid="edit-style-btn"><Pencil size={14} /> Edit details</button>
            <button className="btn btn-ghost btn-sm" onClick={() => setAddVariant(true)} data-testid="add-variant-btn"><Plus size={14} /> Add variant</button>
            <button className="btn btn-ghost btn-sm" onClick={toggleArchive}>{archived ? <><ArchiveRestore size={14} /> Restore</> : <><Archive size={14} /> Archive</>}</button>
          </div>
        )}
      </div>

      {style.variants.length > 0 && <>
        <div className="eyebrow mb-2">Size × Colour Matrix</div>
        <div className="overflow-auto border border-[#E6E1D8] rounded-lg mb-6">
          <table className="tbl">
            <thead>
              <tr><th>Colour \ Size</th>{sizes.map((s) => <th key={s} className="num">{s}</th>)}</tr>
            </thead>
            <tbody>
              {colors.map((c) => (
                <tr key={c}>
                  <td className="font-medium">{c}</td>
                  {sizes.map((s) => {
                    const v = cell(c, s);
                    if (!v) return <td key={s} className="num text-[#D4CEBF]">—</td>;
                    const low = v.current_qty === 0 ? "text-[#A3411D] font-bold" : v.current_qty <= v.reorder_point ? "text-[#B38E46] font-semibold" : "";
                    return <td key={s} className={`num font-num ${low}`}>{v.current_qty}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>}

      <div className="eyebrow mb-2">Variants</div>
      <div className="overflow-auto border border-[#E6E1D8] rounded-lg">
        <table className="tbl">
          <thead>
            <tr>
              <th>Variant</th><th>Barcode</th>{isOwner && <th className="num">Cost</th>}<th className="num">Price</th>
              {isOwner && <th className="num">Margin</th>}<th className="num">GST</th><th className="num">Qty</th><th className="num">Reorder</th><th></th>
            </tr>
          </thead>
          <tbody>
            {style.variants.map((v) => {
              const margin = v.selling_price > 0 ? (((v.selling_price - v.cost_price) / v.selling_price) * 100).toFixed(0) : 0;
              return (
                <tr key={v.id}>
                  <td>{v.color} / {v.size}</td>
                  <td className="font-num text-xs text-[#8C929B]">{v.barcode}</td>
                  {isOwner && <td className="num font-num">{inr(v.cost_price)}</td>}
                  <td className="num font-num">{inr(v.selling_price)}</td>
                  {isOwner && <td className="num font-num text-[#2D5A3C]">{margin}%</td>}
                  <td className="num font-num text-[#8C929B]">{v.tax_rate}%</td>
                  <td className="num font-num">{v.current_qty}</td>
                  <td className="num font-num text-[#8C929B]">{v.reorder_point}</td>
                  <td className="num whitespace-nowrap">
                    {isOwner && <button className="btn btn-ghost btn-sm mr-1" onClick={() => setEditVariant(v)} data-testid={`edit-variant-${v.id}`}>Edit</button>}
                    <button className="btn btn-ghost btn-sm" onClick={() => setAdjust(v)} data-testid={`adjust-${v.id}`}>Adjust</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {style.variants.length === 0 && <Empty>No variants yet — add one to start selling this product.</Empty>}
      </div>
      <AdjustModal open={!!adjust} sku={adjust} onClose={() => setAdjust(null)} onDone={refresh} />
      <VariantForm open={!!editVariant || addVariant} style={style} variant={editVariant} onClose={() => { setEditVariant(null); setAddVariant(false); }} onDone={refresh} />
      <EditStyle style={editStyle} onClose={() => setEditStyle(null)} onDone={refresh} />
    </Modal>
  );
}

export default function Inventory() {
  const { isOwner } = useAuth();
  const settings = useSettings();
  const [styles, setStyles] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const [showArchived, setShowArchived] = useState(false);
  const [selected, setSelected] = useState(null);
  const [adding, setAdding] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = async () => {
    const r = await api.get(`/styles${showArchived ? "" : "?status=active"}`);
    setStyles(r.data);
    const sid = params.get("style");
    if (sid) {
      const s = r.data.find((x) => x.id === sid);
      if (s) setSelected(s);
    }
    return r.data;
  };
  useEffect(() => { load(); }, [showArchived]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (params.get("new") && isOwner) { setAdding(true); setParams({}, { replace: true }); }
  }, [params, isOwner, setParams]);

  const refreshSelected = async () => {
    await load();
    if (selected) {
      const r = await api.get(`/styles/${selected.id}`);
      setSelected(r.data);
    }
  };

  const cats = ["All", ...new Set([...(settings?.product_categories || []), ...styles.map((s) => s.category)].filter(Boolean))];
  const filtered = useMemo(
    () => styles.filter((s) =>
      (cat === "All" || s.category === cat) &&
      (!q || (s.name + s.style_code + (s.brand || "")).toLowerCase().includes(q.toLowerCase()))
    ), [styles, cat, q]);

  const totalCost = filtered.reduce((a, s) => a + s.stock_value_cost, 0);
  const totalRetail = filtered.reduce((a, s) => a + s.stock_value_retail, 0);
  const totalQty = filtered.reduce((a, s) => a + s.total_qty, 0);

  return (
    <div>
      <PageHeader eyebrow="Catalog" title="Inventory" subtitle="One shared pool. Stock is tracked at size × colour — never oversold across counter and online.">
        <div className="text-right mr-2">
          <div className="font-num text-sm">{totalQty} pcs</div>
          {isOwner && <div className="font-num text-xs">{inr0(totalCost)} <span className="text-[#8C929B]">at cost</span></div>}
          <div className="font-num text-xs">{inr0(totalRetail)} <span className="text-[#8C929B]">at retail</span></div>
        </div>
        {isOwner && <button className="btn btn-primary" onClick={() => setAdding(true)} data-testid="add-product-btn"><Plus size={16} /> Add product</button>}
      </PageHeader>

      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8C929B]" />
          <input className="field pl-9" placeholder="Search style, code, brand…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="inventory-search" />
        </div>
        <div className="flex gap-1 bg-white border border-[#E6E1D8] rounded-full p-1 flex-wrap">
          {cats.map((c) => (
            <button key={c} onClick={() => setCat(c)} data-testid={`cat-${c}`}
              className={`px-3.5 py-1.5 rounded-full text-sm transition-colors ${cat === c ? "bg-[#0E1B2E] text-white" : "text-[#5C626A] hover:bg-[#F4F0EA]"}`}>{c}</button>
          ))}
        </div>
        <button className={`btn btn-sm ${showArchived ? "btn-primary" : "btn-ghost"}`} onClick={() => setShowArchived((v) => !v)} data-testid="toggle-archived">
          <SlidersHorizontal size={14} /> {showArchived ? "Including archived" : "Active only"}
        </button>
      </div>

      <div className="card overflow-hidden">
        <table className="tbl">
          <thead>
            <tr><th>Style</th><th>Code</th><th>Category</th><th className="num">Variants</th><th className="num">On hand</th>{isOwner && <th className="num">Value (cost)</th>}<th className="num">Value (retail)</th><th>Status</th></tr>
          </thead>
          <tbody>
            {filtered.map((s) => (
              <tr key={s.id} className="cursor-pointer" onClick={() => setSelected(s)} data-testid={`style-row-${s.style_code}`}>
                <td>
                  <div className="flex items-center gap-3">
                    {s.image && <img src={s.image} alt="" className="w-9 h-11 object-cover rounded border border-[#E6E1D8]" />}
                    <div>
                      <div className="font-medium">{s.name}</div>
                      <div className="text-xs text-[#8C929B]">{s.brand}</div>
                    </div>
                  </div>
                </td>
                <td className="font-num text-xs text-[#8C929B]">{s.style_code}</td>
                <td className="text-[#5C626A]">{s.category} · {s.type}</td>
                <td className="num font-num">{s.variants.length}</td>
                <td className="num font-num">{s.total_qty}</td>
                {isOwner && <td className="num font-num">{inr0(s.stock_value_cost)}</td>}
                <td className="num font-num">{inr0(s.stock_value_retail)}</td>
                <td>{s.status === "archived" ? <StatusChip status="Archived" /> : s.out_of_stock ? <StatusChip status="Out of stock" /> : s.low_stock ? <StatusChip status="Low stock" /> : <StatusChip status="In stock" />}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <Empty>
            {styles.length === 0
              ? <>No products yet.{isOwner && <> <button className="underline text-[#0E1B2E]" onClick={() => setAdding(true)}>Add your first product</button> with its sizes, colours and opening stock.</>}</>
              : "No styles match your filters"}
          </Empty>
        )}
      </div>

      {selected && <StyleDetail style={selected} onClose={() => setSelected(null)} isOwner={isOwner} refresh={refreshSelected} />}
      <AddProduct open={adding} onClose={() => setAdding(false)} onDone={(p) => { load(); setSelected(p); }} />
    </div>
  );
}
